<template>
    <div>
        <div class="row filter-container m-0">
            <FilterModal :modalOpen="modalResubmitOpen">
                <template #heading>
                    <h2>{{ $t('results.resubmit.modal.heading') }}</h2>
                </template>

                <template #headerContent>
                    <span><i class="fal fa-times"></i></span>
                </template>

                <template #content>
                    <Resubmit :loanValues="loanValues" :hidden="!modalResubmitOpen" />
                </template>
            </FilterModal>

            <FilterModal :modalOpen="modalOpen">
                <template #heading>
                    <h2>{{ $t('results.filtering.filters') }}</h2>
                </template>

                <template #headerContent>
                    <span class="header-links save" v-show="filtersModified.length">
                        {{ $t('results.filtering.save') }}
                    </span>

                    <span v-show="!filtersModified.length"><i class="fal fa-times"></i></span>
                </template>

                <template #content>
                    <FilterMonthlyPaymentRange v-if="showPaymentRange()" />

                    <FilterTerm v-if="showTerm()" />

                    <FilterLenders v-if="!creditCardCampaign" />

                    <FilterOfferType v-if="offerResultDataFilters.offer_types.length > 1 && !isVervent" />

                    <FilterFees v-if="showFees" ref="fees" class="d-lg-none" />
                </template>

                <template #footerContent="{ clearAllFilters }">
                    <span v-if="filtersActive.length" class="d-none d-lg-inline clear-filters" @click="clearAllFilters">
                        <i class="fal fa-times"></i> {{ $t('results.filtering.clearAllFilters') }}
                    </span>

                    <div v-if="showFees" class="custom-control custom-switch d-none d-lg-block">
                        <input
                            type="checkbox"
                            class="custom-control-input"
                            id="customSwitch1"
                            :checked="!withoutFeesChecked"
                            @click="toggleFees"
                        />

                        <label class="custom-control-label" for="customSwitch1">
                            {{ $t('results.filtering.withFees') }}
                        </label>
                    </div>
                </template>
            </FilterModal>

            <FilterModal :modalOpen="modalSortOpen">
                <template #heading>
                    <h2>{{ $t('results.sorting.sort') }}</h2>
                </template>

                <template #headerContent>
                    <span><i class="fal fa-times"></i></span>
                </template>

                <template #content="{ close }">
                    <SortOptions @close-sort-modal="close" />
                </template>
            </FilterModal>
        </div>

        <div class="container filter-wrapper p-md-0">
            <div class="col d-flex justify-content-center justify-content-md-end p-0">
                <div class="row filter-container">
                    <SelectGrid v-if="canShowABTestSortBlocks" :sortOptions="sortOptions" />
                    <div
                        class="filter-column py-md-0 py-2"
                        :class="{
                            'col-md-4 offset-md-8': !canShowABTestSortBlocks,
                            'col-md-4 offset-md-2': canShowABTestSortBlocks,
                        }"
                    >
                        <div class="filter-tool">
                            <div
                                class="update-search-button"
                                v-if="!isAgentPage && status !== 'expired' && resubmitEnabled && !creditCardCampaign"
                                @click="openResubmitModal()"
                            >
                                <div class="inner update">
                                    <img :src="asset('/v2/icons/update.svg')" alt="Update Search" class="icon-left" />
                                    <div class="filter-button-text">
                                        <span v-if="expired">{{ $t('results.filtering.refresh') }}</span>
                                        <span v-else>{{ $t('results.filtering.update') }}</span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="!isVervent && showFilterFlyout" class="filter-button" @click="openFilterModal()">
                                <div class="inner update">
                                    <img :src="asset('/v2/icons/filters.svg')" alt="Filters" class="icon-left" />
                                    <div class="filter-button-text">
                                        {{ $t('results.filtering.filters') }}
                                        <a v-if="filtersActive.length" class="filter-count">{{
                                            filtersActive.length
                                        }}</a>
                                    </div>
                                </div>
                            </div>

                            <div v-if="!isVervent && showFilterFlyout" class="sort-button" @click="openSortModal()">
                                <div class="inner">
                                    <i class="far fa-arrow-up-arrow-down"></i>
                                    <div class="filter-button-text">{{ $t('results.sorting.sort') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { useRoute, useRouter } from 'vue-router'

// lodash components
import filter from 'lodash/filter'

export default {
    setup() {
        const route = useRoute()
        const router = useRouter()

        return {
            route,
            router,
        }
    },
    props: ['isAgentPage', 'offersHaveFees', 'offers'],
    data() {
        return {
            modalOpen: false,
            modalSortOpen: false,
            modalResubmitOpen: false,
            filterOverride: {},
            stickyOverride: {},
            topOffset: null,
            iframeAllowedSites: [],
            modalOverlay: false,
        }
    },
    mounted() {
        this.emitter.on('closeFilterModal', this.closeModal)
    },
    computed: {
        ...mapGetters([
            'sortOptions',
            'offerResultDataFilters',
            'expired',
            'filtersModified',
            'filtersActive',
            'loanValues',
        ]),
        showFilterFlyout: function () {
            return parseInt(this.themeSetting('hide_product_type_filter')) !== 1
        },
        currentInstance: function () {
            return this
        },
        withoutFeesChecked() {
            return this.$store.state.result.withoutFeesChecked
        },
        showFees: function () {
            return window.TERRITORY !== 'pol' && this.offersHaveFees
        },
        territory: function () {
            return process.env.TERRITORY
        },
        resubmitEnabled: function () {
            return this.$store.state.global.data.resubmit_show_form === '1'
        },
        isResultPage: function () {
            const currentPath = this.route.fullPath
            const regex = /result\/([a-f0-9]{32})$/
            const matches = regex.exec(currentPath)
            return !!matches
        },
        status() {
            return this.$store.state.result.status
        },
        isCreditCardOnly: function () {
            return filter(this.offers, offer => {
                return offer.type.includes('credit card only', 'credit_card')
            })
        },
        creditCardCampaign: function () {
            return window.CAMPAIGN_TYPE === 'Credit Card'
        },
        isVervent: function () {
            return process.env.IS_VERVENT
        },
        canShowABTestSortBlocks: function () {
            // TODO - Add back when test is finialised
            return false
            const abTestingSortBlocks = process.env.AB_TESTING_SORT_BLOCKS
            if (abTestingSortBlocks !== 'true') {
                return false
            }

            if (this.offers.length < 4) {
                return false
            }

            return this.$store.state.result.offerResultData.fastFundingFlagStatus
        },
    },
    methods: {
        toggleFees() {
            if (this.$refs.fees) {
                this.$refs.fees.toggle()
            }
        },
        openFilterModal: function () {
            this.modalOpen = true
        },
        openSortModal: function () {
            this.modalSortOpen = true
        },
        openResubmitModal: function () {
            if (this.expired) {
                this.emitter.emit('refreshOffer', {
                    result: this.currentInstance,
                })
            } else {
                this.modalResubmitOpen = true
            }
        },
        closeModal: function () {
            this.modalOpen = false
            this.modalSortOpen = false
            this.modalResubmitOpen = false
        },
        showTerm: function () {
            return (
                !this.offerResultDataFilters.offer_types.includes('credit card only') &&
                this.offerResultDataFilters.loan_terms.length > 0 &&
                !this.isCreditCardOnly.length > 0
            )
        },
        showPaymentRange: function () {
            return (
                this.offerResultDataFilters.loan_amount.min != this.offerResultDataFilters.loan_amount.max &&
                !this.isCreditCardOnly.length > 0
            )
        },
    },
}
</script>

<style lang="scss">
@import '~scss/mixins/gutter-size';
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';
@import '~scss/settings/variables';

.filter-container {
    margin: 30px 0;

    @media screen and (min-width: $bp-small) {
        margin: 16px 0;
        .active-eligibility-bg,
        .eligibility-bg {
            .amount {
                font-size: 1.1111111111rem;
                font-weight: 600;
            }
        }
    }

    @media screen and (max-width: $bp-small) {
        margin: 16px 0;
        .active-eligibility-bg,
        .eligibility-bg {
            .amount {
                font-size: rem(16px);
                font-weight: 600;
            }
            .title {
                font-size: rem(11px);
                font-weight: 600;
            }
            .repayment {
                font-size: rem(12px);
            }
        }
    }

    .active-eligibility-bg {
        background-color: var(--wizard-button-color);
        font-size: 0.7777777778rem;
        font-style: normal;
        border-radius: 10px;
        .title,
        .amount,
        .repayment,
        .apr {
            color: var(--wizard-button-text-color);
        }
        .title {
            font-weight: 600;
        }
        cursor: pointer;
    }

    .eligibility-bg {
        font-size: 0.7777777778rem;
        color: #ffffff;
        font-style: normal;
        border: 1px solid $color-grey;
        border-radius: 10px;
        .title,
        .repayment,
        .amount .apr {
            color: #000000;
        }
        .title {
            font-weight: 600;
        }
        cursor: pointer;
    }
}

.filter-wrapper {
    @media screen and (min-width: $bp-small) {
        .filter-tool {
            max-width: 300px;
        }
    }

    .filter-tool {
        height: 40px;
        background-color: var(--filter-menu-background);
        color: #ffffff;
        font-size: 15px;
        line-height: 18px;
        letter-spacing: 0.01em;
        text-align: center;
        border-radius: 25px;
        display: flex;

        .filter-button,
        .update-search-button,
        .sort-button {
            cursor: pointer;
            padding-top: 10px;
            padding-bottom: 10px;
            min-width: 100px;

            .inner {
                color: #ffffff;
                display: flex;
                justify-content: center;

                &.update {
                    border-right: 1px #ffffff solid;
                }

                &.sort {
                    border-left: 1px #ffffff solid;
                }

                .filter-button-text {
                    padding-top: 1px;
                    margin-left: 5px;
                    color: #ffffff;
                    display: flex;

                    .filter-count {
                        margin-top: 3px;
                        display: inline-block;
                        font-size: 11px;
                        border-radius: 50%;
                        color: #2b2b2b;
                        background: #a7a7a7;
                        width: 13px;
                        height: 13px;
                        line-height: 12px;
                        position: relative;
                        margin-left: 4px;
                        text-align: center;
                        font-weight: 700;
                    }
                }
            }
        }
    }
}

@media screen and (min-width: $bp-small) {
    .filter-column {
        display: flex;
        justify-content: flex-end;
        padding: 15px 0;
    }
}
.modal-overlay {
    background: rgba(0, 0, 0, 0.6);
    height: 100%;
    width: 100%;
    position: fixed;
    top: 0;
    left: 0;
    z-index: 10;
}
</style>
